import React, { useState, useEffect, useMemo } from 'react';
import { Spinner } from 'react-bootstrap';
import axios from 'axios';
import { useEntidadeContext } from '../../EntidadeContext';
import { useAlertModal } from '../../AlertModalContext';
import { useAuthContext } from '../../context/AuthContext';

const initialConfigState = {
  email_host: '', email_port: '', email_user: '', email_pass: '',
  whatsapp_token: '', whatsapp_instance_id: '',
};

/* ── helpers visuais ── */
const inputBase = {
  width: '100%', padding: '9px 12px', borderRadius: '8px',
  border: '1px solid #e5e7eb', fontSize: `calc(13px * var(--font-scale))`, color: '#374151',
  fontFamily: 'inherit', outline: 'none', backgroundColor: '#fff',
  boxSizing: 'border-box', transition: 'border-color .15s, box-shadow .15s',
};
const inputDisabled = { ...inputBase, backgroundColor: '#f9fafb', color: '#9ca3af', cursor: 'not-allowed' };
const fh = {
  onFocus: e => { e.target.style.borderColor = '#2563eb'; e.target.style.boxShadow = '0 0 0 3px rgba(37,99,235,.1)'; },
  onBlur:  e => { e.target.style.borderColor = '#e5e7eb'; e.target.style.boxShadow = 'none'; },
};
const Label = ({ children }) => (
  <label style={{ fontSize: `calc(11px * var(--font-scale))`, fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.5px', display: 'block', marginBottom: '5px' }}>
    {children}
  </label>
);
const Field = ({ label, children, hint }) => (
  <div style={{ marginBottom: '16px' }}>
    <Label>{label}</Label>
    {children}
    {hint && <div style={{ fontSize: `calc(11px * var(--font-scale))`, color: '#9ca3af', marginTop: '5px' }}>{hint}</div>}
  </div>
);

const ICONS = {
  mail:      'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
  whatsapp:  'M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0',
  settings:  'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z',
  check:     'M5 13l4 4L19 7',
  flask:     'M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18',
  save:      'M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4',
};

const Btn = ({ children, onClick, disabled, loading, variant = 'primary', type = 'button' }) => {
  const variants = {
    primary:   { bg: 'linear-gradient(135deg,#1e3a5f,#2563eb)', color: '#fff', border: 'none' },
    success:   { bg: 'linear-gradient(135deg,#14532d,#16a34a)', color: '#fff', border: 'none' },
    secondary: { bg: '#fff', color: '#374151', border: '1px solid #e5e7eb' },
    warning:   { bg: 'linear-gradient(135deg,#78350f,#d97706)', color: '#fff', border: 'none' },
  };
  const v = variants[variant] || variants.primary;
  return (
    <button
      type={type} onClick={onClick} disabled={disabled || loading}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '6px',
        padding: '9px 18px', borderRadius: '9px', border: v.border,
        background: (disabled || loading) ? '#e5e7eb' : v.bg,
        color: (disabled || loading) ? '#9ca3af' : v.color,
        fontSize: `calc(13px * var(--font-scale))`, fontWeight: '600', fontFamily: 'inherit',
        cursor: (disabled || loading) ? 'not-allowed' : 'pointer', transition: 'opacity .15s',
      }}
    >
      {loading && <Spinner as="span" animation="border" size="sm" style={{ width: '13px', height: '13px', borderWidth: '2px' }} />}
      {children}
    </button>
  );
};

const StatusPill = ({ complete, completeLabel = 'Completo', incompleteLabel = 'Incompleto', comingSoon }) => {
  if (comingSoon) return (
    <span style={{ fontSize: `calc(10px * var(--font-scale))`, fontWeight: '700', padding: '2px 8px', borderRadius: '999px', backgroundColor: '#f3f4f6', color: '#6b7280', border: '1px solid #e5e7eb' }}>Em breve</span>
  );
  return (
    <span style={{ fontSize: `calc(10px * var(--font-scale))`, fontWeight: '700', padding: '2px 8px', borderRadius: '999px', backgroundColor: complete ? '#f0fdf4' : '#fffbeb', color: complete ? '#16a34a' : '#d97706', border: `1px solid ${complete ? '#bbf7d0' : '#fde68a'}` }}>
      {complete ? `✓ ${completeLabel}` : `⚠ ${incompleteLabel}`}
    </span>
  );
};

const ComunicadosConfigTab = () => {
  const { currentEntidade } = useEntidadeContext();
  const { showAlertModal }  = useAlertModal();
  const { loggedInUser: currentUser } = useAuthContext();

  const [config, setConfig]           = useState(initialConfigState);
  const [initialConfig, setInitialConfig] = useState(initialConfigState);
  const [isLoading, setIsLoading]     = useState(false);
  const [isTesting, setIsTesting]     = useState(false);
  const [isTestingWA, setIsTestingWA] = useState(false);
  const [emailTestOk, setEmailTestOk] = useState(false);

  const isEmailComplete   = useMemo(() => !!(config.email_host && config.email_port && config.email_user && config.email_pass), [config]);
  const isWhatsAppComplete = useMemo(() => !!(config.whatsapp_token && config.whatsapp_instance_id), [config]);
  const emailChanged = useMemo(() => (
    config.email_host !== initialConfig.email_host ||
    String(config.email_port) !== String(initialConfig.email_port) ||
    config.email_user !== initialConfig.email_user ||
    config.email_pass !== initialConfig.email_pass
  ), [config, initialConfig]);
  const isChanged = useMemo(() => JSON.stringify(config) !== JSON.stringify(initialConfig), [config, initialConfig]);

  useEffect(() => {
    const fetchConfig = async () => {
      if (!currentEntidade) return;
      setIsLoading(true); setEmailTestOk(false);
      try {
        const response = await axios.get(`/api/entidades/${currentEntidade.id}/comunicacao-config`);
        const fetched = response.data.config || initialConfigState;
        setConfig(fetched); setInitialConfig(fetched);
      } catch {
        setConfig(initialConfigState); setInitialConfig(initialConfigState);
        showAlertModal('Erro', 'Não foi possível carregar as configurações de comunicação.', 'warning');
      } finally { setIsLoading(false); }
    };
    fetchConfig();
  }, [currentEntidade, showAlertModal]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setConfig(prev => ({ ...prev, [name]: value }));
    if (['email_host', 'email_port', 'email_user', 'email_pass'].includes(name)) setEmailTestOk(false);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!currentEntidade) return;
    setIsLoading(true);
    try {
      const response = await axios.post(`/api/entidades/${currentEntidade.id}/comunicacao-config`, config);
      showAlertModal('Sucesso', 'Configurações salvas com sucesso!', 'success');
      const saved = response.data.config || initialConfigState;
      setConfig(saved); setInitialConfig(saved); setEmailTestOk(false);
    } catch (err) {
      showAlertModal('Erro', `Falha ao salvar: ${err.response?.data?.message || 'Erro desconhecido.'}`, 'danger');
    } finally { setIsLoading(false); }
  };

  const handleTestEmail = async () => {
    if (!isEmailComplete) { showAlertModal('Atenção', 'Preencha todos os campos de e-mail antes de testar.', 'warning'); return; }
    if (!currentUser?.email) { showAlertModal('Erro', 'Não foi possível identificar o e-mail do usuário logado.', 'danger'); return; }
    setIsTesting(true); setEmailTestOk(false);
    try {
      await axios.post('/api/test-email-connection', {
        host: config.email_host, port: config.email_port,
        username: config.email_user, password: config.email_pass,
        recipient_email: currentUser.email, entidade_id: currentEntidade.id,
      });
      showAlertModal('Sucesso', `E-mail de teste enviado para ${currentUser.email}.`, 'success');
      setEmailTestOk(true);
    } catch (err) {
      showAlertModal('Erro de Conexão', err.response?.data?.message || 'Falha na conexão SMTP.', 'danger');
    } finally { setIsTesting(false); }
  };

  const handleTestWhatsApp = async () => {
    if (!isWhatsAppComplete) { showAlertModal('Atenção', 'Preencha Token e ID da Instância antes de testar.', 'warning'); return; }
    if (!currentUser?.dddcelular || !currentUser?.celular) { showAlertModal('Erro', 'Número de telefone do usuário não encontrado.', 'danger'); return; }
    setIsTestingWA(true);
    try {
      const phone = `55${currentUser.dddcelular}${currentUser.celular}`;
      await axios.post('/api/test-whatsapp-connection', { token: config.whatsapp_token, instance_id: config.whatsapp_instance_id, recipient_phone: phone });
      showAlertModal('Sucesso', `Mensagem de teste enviada para ${phone}.`, 'success');
    } catch (err) {
      showAlertModal('Erro de Conexão', err.response?.data?.message || 'Falha na conexão WhatsApp.', 'danger');
    } finally { setIsTestingWA(false); }
  };

  const canSave = isChanged && !(emailChanged && !emailTestOk);

  return (
    <div style={{ fontFamily: "'Inter','Segoe UI',sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');`}</style>

      {/* ── HEADER ── */}
      <div style={{ background: 'linear-gradient(135deg,#1e3a5f,#2563eb)', borderRadius: '14px', padding: '20px 24px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '14px' }}>
        <div style={{ width: '44px', height: '44px', borderRadius: '12px', backgroundColor: 'rgba(255,255,255,.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <svg width="22" height="22" fill="none" stroke="#fff" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d={ICONS.mail} />
          </svg>
        </div>
        <div>
          <div style={{ fontSize: '16px', fontWeight: '800', color: '#fff' }}>Configurações de Comunicação</div>
          <div style={{ fontSize: `calc(12px * var(--font-scale))`, color: 'rgba(255,255,255,.7)' }}>Configure o envio de e-mail e acesse a central WhatsApp</div>
        </div>
      </div>

      {/* ── INFO ── */}
      <div style={{ backgroundColor: '#eff6ff', borderRadius: '12px', border: '1px solid #bfdbfe', padding: '14px 18px', marginBottom: '24px', display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
        <svg width="18" height="18" fill="none" stroke="#2563eb" viewBox="0 0 24 24" style={{ flexShrink: 0, marginTop: '1px' }}>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <div style={{ fontSize: `calc(12px * var(--font-scale))`, color: '#3b82f6', lineHeight: '1.6' }}>
          <strong style={{ color: '#1e40af' }}>Para que servem estas configurações?</strong><br />
          As configurações de <strong>E-mail (SMTP)</strong> e <strong>WhatsApp</strong> permitem que o sistema envie comunicados, lembretes de agendamento e promoções para seus pacientes. Preencha os dados e realize o teste de conexão antes de salvar.
        </div>
      </div>

      {isLoading ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '48px 0', gap: '12px' }}>
          <Spinner animation="border" variant="primary" style={{ width: '28px', height: '28px' }} />
          <span style={{ fontSize: `calc(13px * var(--font-scale))`, color: '#6b7280' }}>Carregando configurações...</span>
        </div>
      ) : (
        <form onSubmit={handleSave}>

          {/* ── BLOCO E-MAIL ── */}
          <div style={{ backgroundColor: '#fff', borderRadius: '14px', border: '1px solid #e5e7eb', padding: '24px', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px', paddingBottom: '14px', borderBottom: '2px solid #f1f5f9' }}>
              <div style={{ width: '28px', height: '28px', borderRadius: '7px', background: 'linear-gradient(135deg,#1e3a5f,#2563eb)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="14" height="14" fill="none" stroke="#fff" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={ICONS.mail} />
                </svg>
              </div>
              <span style={{ fontSize: `calc(13px * var(--font-scale))`, fontWeight: '700', color: '#1e3a5f' }}>Configurações de E-mail (SMTP)</span>
              <StatusPill complete={isEmailComplete} />
              {emailTestOk && (
                <span style={{ fontSize: `calc(10px * var(--font-scale))`, fontWeight: '700', padding: '2px 8px', borderRadius: '999px', backgroundColor: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' }}>
                  ✓ Conexão testada
                </span>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 180px', gap: '0 20px' }}>
              <Field label="Servidor SMTP (Host)">
                <input name="email_host" type="text" value={config.email_host} onChange={handleChange}
                  style={inputBase} placeholder="smtp.seuprovedor.com" {...fh} />
              </Field>
              <Field label="Porta">
                <input name="email_port" type="text" value={config.email_port} onChange={handleChange}
                  style={inputBase} placeholder="587" {...fh} />
              </Field>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 20px' }}>
              <Field label="Usuário / E-mail">
                <input name="email_user" type="text" value={config.email_user} onChange={handleChange}
                  style={inputBase} placeholder="seu_email@provedor.com" {...fh} />
              </Field>
              <Field
                label="Senha"
                hint={<>Usa Gmail? <a href="https://support.google.com/mail/answer/185833?hl=pt-BR" target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb' }}>Crie uma senha de app</a>.</>}
              >
                <input name="email_pass" type="password" value={config.email_pass} onChange={handleChange}
                  style={inputBase} placeholder="••••••••" {...fh} />
              </Field>
            </div>

            {/* Aviso de teste obrigatório */}
            {emailChanged && !emailTestOk && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', borderRadius: '9px', backgroundColor: '#fffbeb', border: '1px solid #fde68a', marginTop: '4px' }}>
                <svg width="15" height="15" fill="none" stroke="#d97706" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <span style={{ fontSize: `calc(12px * var(--font-scale))`, color: '#92400e' }}>
                  Você alterou as configurações de e-mail. <strong>Teste a conexão</strong> antes de salvar.
                </span>
              </div>
            )}
          </div>

          {/* ── BLOCO WHATSAPP ── */}
          <div style={{ backgroundColor: '#fff', borderRadius: '14px', border: '1px solid #e5e7eb', padding: '24px', marginBottom: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px', paddingBottom: '14px', borderBottom: '2px solid #f1f5f9' }}>
              <div style={{ width: '28px', height: '28px', borderRadius: '7px', background: 'linear-gradient(135deg,#14532d,#16a34a)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="14" height="14" fill="none" stroke="#fff" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={ICONS.whatsapp} />
                </svg>
              </div>
              <span style={{ fontSize: `calc(13px * var(--font-scale))`, fontWeight: '700', color: '#1e3a5f' }}>Configurações de WhatsApp</span>
              <span className="badge bg-success">Conexões integradas</span>
            </div>

            <p>O WhatsApp utiliza as conexões já cadastradas. Configure mensagens, automações e testes na <a href="/comunicados">Central de comunicação</a>.</p>
          </div>

          {/* ── AÇÕES ── */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', flexWrap: 'wrap' }}>
            <Btn onClick={handleTestEmail} disabled={!isEmailComplete} loading={isTesting} variant="secondary">
              <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={ICONS.flask} />
              </svg>
              {isTesting ? 'Testando...' : 'Testar E-mail'}
            </Btn>

            <Btn
              type="submit"
              disabled={!canSave || isLoading}
              loading={isLoading}
              variant="success"
            >
              <svg width="13" height="13" fill="none" stroke="#fff" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d={ICONS.save} />
              </svg>
              {isLoading ? 'Salvando...' : 'Salvar Configurações'}
            </Btn>
          </div>
        </form>
      )}
    </div>
  );
};

export default ComunicadosConfigTab;
